/**
 * 下限・上限 select の逆転を防ぐ（選択不可 option は非表示）
 * data-range-select コンテナ内の data-range-min / data-range-max をペアで制御
 * data-range-required … 先頭「選択してください」(value="") をプレースホルダとして扱う（必須バリデーション用）
 * data-range-placeholder … 同上のプレースホルダ UI のみ（任意フォーム向け）
 */

/** @typedef {{ value: string, label: string, isOpenBound: boolean, isPlaceholder: boolean }} RangeOptionDef */

/** @type {WeakMap<Element, { minDefs: RangeOptionDef[], maxDefs: RangeOptionDef[], emptyValues: Set<string>, placeholderValues: Set<string> }>} */
const rangeStore = new WeakMap();

/** 必須フォームのプレースホルダ value */
export const RANGE_PLACEHOLDER_VALUE = '';

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isRangePlaceholderValue(value) {
	return value === RANGE_PLACEHOLDER_VALUE;
}

/**
 * @param {Element} container
 * @returns {Set<string>}
 */
function getPlaceholderValues(container) {
	if (
		container.hasAttribute('data-range-required') ||
		container.hasAttribute('data-range-placeholder')
	) {
		return new Set([RANGE_PLACEHOLDER_VALUE]);
	}
	return new Set();
}

/**
 * @param {string} value
 * @param {Set<string>} emptyValues
 * @param {Set<string>} placeholderValues
 * @returns {number | null}
 */
function parseRangeValue(value, emptyValues, placeholderValues) {
	if (placeholderValues.has(value) || emptyValues.has(value)) return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}

/**
 * @param {Element} container
 * @returns {Set<string>}
 */
function getEmptyValues(container) {
	if (container.dataset.rangeEmpty === 'none') {
		return new Set(['none']);
	}
	return new Set(['', 'none']);
}

/**
 * @param {HTMLSelectElement} select
 * @param {Set<string>} emptyValues
 * @param {Set<string>} placeholderValues
 * @returns {RangeOptionDef[]}
 */
function captureOptions(select, emptyValues, placeholderValues) {
	return Array.from(select.options).map((opt) => ({
		value: opt.value,
		label: opt.textContent ?? '',
		isOpenBound: emptyValues.has(opt.value),
		isPlaceholder: placeholderValues.has(opt.value),
	}));
}

/**
 * @param {RangeOptionDef[]} defs
 * @returns {string | undefined}
 */
function getFallbackValue(defs) {
	const placeholder = defs.find((def) => def.isPlaceholder);
	if (placeholder) return placeholder.value;
	const openBound = defs.find((def) => def.isOpenBound);
	if (openBound) return openBound.value;
	return defs[0]?.value;
}

/**
 * @param {HTMLSelectElement} select
 * @param {RangeOptionDef[]} defs
 * @param {Set<string>} emptyValues
 * @param {Set<string>} placeholderValues
 * @param {(n: number | null) => boolean} isVisible
 * @param {string} preferredValue
 * @returns {string}
 */
function applyOptions(select, defs, emptyValues, placeholderValues, isVisible, preferredValue) {
	const visible = defs.filter((def) => {
		if (def.isPlaceholder || def.isOpenBound) return true;
		return isVisible(parseRangeValue(def.value, emptyValues, placeholderValues));
	});

	select.replaceChildren(
		...visible.map((def) => {
			const opt = document.createElement('option');
			opt.value = def.value;
			opt.textContent = def.label;
			return opt;
		}),
	);

	const visibleValues = new Set(visible.map((def) => def.value));
	if (visibleValues.has(preferredValue)) {
		select.value = preferredValue;
		return preferredValue;
	}

	const fallback = getFallbackValue(defs);
	if (fallback != null && visibleValues.has(fallback)) {
		select.value = fallback;
		return fallback;
	}

	return select.value;
}

/**
 * @param {HTMLSelectElement} minSelect
 * @param {HTMLSelectElement} maxSelect
 * @param {RangeOptionDef[]} minDefs
 * @param {RangeOptionDef[]} maxDefs
 * @param {Set<string>} emptyValues
 * @param {Set<string>} placeholderValues
 */
function syncRangePair(minSelect, maxSelect, minDefs, maxDefs, emptyValues, placeholderValues) {
	let minVal = minSelect.value;
	let maxVal = maxSelect.value;

	maxVal = applyOptions(
		maxSelect,
		maxDefs,
		emptyValues,
		placeholderValues,
		(n) => {
			const minN = parseRangeValue(minVal, emptyValues, placeholderValues);
			return minN === null || n === null || n > minN;
		},
		maxVal,
	);

	minVal = applyOptions(
		minSelect,
		minDefs,
		emptyValues,
		placeholderValues,
		(n) => {
			const maxN = parseRangeValue(maxVal, emptyValues, placeholderValues);
			return maxN === null || n === null || n < maxN;
		},
		minVal,
	);

	applyOptions(
		maxSelect,
		maxDefs,
		emptyValues,
		placeholderValues,
		(n) => {
			const minN = parseRangeValue(minVal, emptyValues, placeholderValues);
			return minN === null || n === null || n > minN;
		},
		maxVal,
	);
}

/**
 * @param {Element} container
 */
export function syncRangeSelectContainer(container) {
	const stored = rangeStore.get(container);
	if (!stored) return;

	const minSelect = container.querySelector('[data-range-min]');
	const maxSelect = container.querySelector('[data-range-max]');
	if (!(minSelect instanceof HTMLSelectElement) || !(maxSelect instanceof HTMLSelectElement)) {
		return;
	}

	syncRangePair(
		minSelect,
		maxSelect,
		stored.minDefs,
		stored.maxDefs,
		stored.emptyValues,
		stored.placeholderValues,
	);
}

/**
 * @param {ParentNode} [root]
 */
export function resyncRangeSelectControls(root = document) {
	root.querySelectorAll('[data-range-select]').forEach((container) => {
		syncRangeSelectContainer(container);
	});
}

/**
 * @param {ParentNode} [root]
 */
export function initRangeSelectControls(root = document) {
	root.querySelectorAll('[data-range-select]').forEach((container) => {
		const minSelect = container.querySelector('[data-range-min]');
		const maxSelect = container.querySelector('[data-range-max]');
		if (!(minSelect instanceof HTMLSelectElement) || !(maxSelect instanceof HTMLSelectElement)) {
			return;
		}

		const emptyValues = getEmptyValues(container);
		const placeholderValues = getPlaceholderValues(container);
		const minDefs = captureOptions(minSelect, emptyValues, placeholderValues);
		const maxDefs = captureOptions(maxSelect, emptyValues, placeholderValues);
		rangeStore.set(container, { minDefs, maxDefs, emptyValues, placeholderValues });

		const sync = () => syncRangeSelectContainer(container);

		minSelect.addEventListener('change', sync);
		maxSelect.addEventListener('change', sync);
		sync();
	});
}
